import type {
    BeneficioClube,
    ClubeBeneficiosApi,
    MensalidadeClube,
    ObterClubeResposta,
    ResumoFinanceiroClube,
    SalvarClubeItemPayload,
    SalvarClubePayload,
} from "../../../services/clubeBeneficios"

export type { MensalidadeClube, ResumoFinanceiroClube }

// Campos numéricos ficam como string no formulário (aceitam "10,50") e viram number no payload.
export type BeneficioForm = {
    chave: string // identificador local (React key), estável mesmo sem id
    id: number | null
    tipo: "CUPOM"
    quantidade: string
    valor: string
    percentual: boolean
    valorMinimoPedido: string
    maximoDesconto: string
    ativo: boolean
}

/** Um plano (clube) da loja. Ex.: R$ 10/mês → 4 cupons; R$ 20/mês → 8 cupons. */
export type PlanoClubeForm = {
    chave: string // identificador local (React key), estável mesmo sem id
    id: number | null
    ativo: boolean
    nome: string
    descricao: string
    valorMensal: string
    beneficios: BeneficioForm[]
    deletarBeneficioIds: number[]
    proximaChave: number // para chaves de benefícios novos
}

export type ClubeBeneficiosModel = {
    planos: PlanoClubeForm[]
    /** planos já salvos que o lojista removeu: vão em deletarClubeIds no salvar e podem ser restaurados */
    planosExcluidos: PlanoClubeForm[]
    recebedorConfigurado: boolean
    proximaChave: number // para chaves de planos novos
}

export const QUANTIDADE_MAXIMA_CUPONS = 1000

export function novoPlano(chave: number): PlanoClubeForm {
    return {
        chave: `novo-${chave}`,
        id: null,
        ativo: false,
        nome: chave === 1 ? "Clube de Benefícios" : "",
        descricao: "",
        valorMensal: "",
        beneficios: [],
        deletarBeneficioIds: [],
        proximaChave: 1,
    }
}

export const ClubeBeneficiosInicial: ClubeBeneficiosModel = {
    planos: [novoPlano(1)],
    planosExcluidos: [],
    recebedorConfigurado: false,
    proximaChave: 2,
}

export function novoBeneficio(chave: number): BeneficioForm {
    return {
        chave: `novo-${chave}`,
        id: null,
        tipo: "CUPOM",
        quantidade: "1",
        valor: "",
        percentual: false,
        valorMinimoPedido: "",
        maximoDesconto: "",
        ativo: true,
    }
}

/** "10,50" / "10.50" / " 10 " → 10.5; vazio ou inválido → null */
export function paraNumero(valor: string | number | null | undefined): number | null {
    if (valor === null || valor === undefined) return null
    if (typeof valor === "number") return Number.isFinite(valor) ? valor : null
    const s = valor.trim().replace(/\s/g, "")
    if (!s) return null
    // "1.234,56" → "1234.56"; "10,5" → "10.5"
    const normalizado = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s
    const n = Number(normalizado)
    return Number.isFinite(n) ? n : null
}

function numeroParaTexto(n: number | null | undefined): string {
    if (n === null || n === undefined || !Number.isFinite(n)) return ""
    return String(n).replace(".", ",")
}

export function formatarMoeda(n: number | null | undefined): string {
    const v = n ?? 0
    return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
}

function planoDoBackend(clube: ClubeBeneficiosApi, indice: number): PlanoClubeForm {
    const beneficios = [...(clube.beneficios ?? [])]
        .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
        .map((b, i) => ({
            chave: b.id ? `id-${b.id}` : `novo-${i + 1}`,
            id: b.id ?? null,
            tipo: "CUPOM" as const,
            quantidade: numeroParaTexto(b.quantidade),
            valor: numeroParaTexto(b.valor),
            percentual: !!b.percentual,
            valorMinimoPedido: numeroParaTexto(b.valorMinimoPedido),
            maximoDesconto: numeroParaTexto(b.maximoDesconto),
            ativo: b.ativo !== false,
        }))
    return {
        chave: clube.id ? `id-${clube.id}` : `carregado-${indice + 1}`,
        id: clube.id ?? null,
        ativo: !!clube.ativo,
        nome: clube.nome ?? "",
        descricao: clube.descricao ?? "",
        valorMensal: numeroParaTexto(clube.valorMensal),
        beneficios,
        deletarBeneficioIds: [],
        proximaChave: beneficios.length + 1,
    }
}

export function modeloDoBackend(resposta: Partial<ObterClubeResposta>): ClubeBeneficiosModel {
    const recebedorConfigurado = !!resposta.recebedorConfigurado
    // v2 manda `clubes`; um backend v1 só manda `clube`.
    const clubes = resposta.clubes ?? (resposta.clube ? [resposta.clube] : [])
    if (clubes.length === 0) {
        return { ...ClubeBeneficiosInicial, recebedorConfigurado }
    }
    const ordenados = clubes
        .map((c, i) => ({ c, i }))
        .sort(
            (a, b) =>
                (a.c.ordem ?? 0) - (b.c.ordem ?? 0) ||
                (a.c.valorMensal ?? 0) - (b.c.valorMensal ?? 0) ||
                a.i - b.i
        )
    const planos = ordenados.map(({ c }, i) => planoDoBackend(c, i))
    return {
        planos,
        planosExcluidos: [],
        recebedorConfigurado,
        proximaChave: planos.length + 1,
    }
}

function planoParaPayload(plano: PlanoClubeForm, ordem: number): SalvarClubeItemPayload {
    const beneficios: BeneficioClube[] = plano.beneficios.map((b, i) => {
        const item: BeneficioClube = {
            tipo: "CUPOM",
            quantidade: Math.trunc(paraNumero(b.quantidade) ?? 0),
            valor: paraNumero(b.valor) ?? 0,
            percentual: b.percentual,
            valorMinimoPedido: paraNumero(b.valorMinimoPedido),
            maximoDesconto: b.percentual ? paraNumero(b.maximoDesconto) : null,
            ativo: b.ativo,
            ordem: i,
        }
        if (b.id) item.id = b.id
        return item
    })
    const item: SalvarClubeItemPayload = {
        ativo: plano.ativo,
        nome: plano.nome.trim(),
        descricao: plano.descricao.trim(),
        valorMensal: paraNumero(plano.valorMensal) ?? 0,
        ordem,
        beneficios,
        deletarBeneficioIds: [...plano.deletarBeneficioIds],
    }
    if (plano.id) item.id = plano.id
    return item
}

export function montarPayload(dados: ClubeBeneficiosModel): SalvarClubePayload {
    return {
        clubes: dados.planos.map((p, i) => planoParaPayload(p, i)),
        deletarClubeIds: dados.planosExcluidos
            .map(p => p.id)
            .filter((id): id is number => !!id),
    }
}

/** "Plano 2 (Ouro)" — usado como prefixo das mensagens de validação. */
export function rotuloPlano(plano: PlanoClubeForm, indice: number): string {
    const nome = plano.nome.trim()
    return nome ? `Plano ${indice + 1} (${nome})` : `Plano ${indice + 1}`
}

function validarPlano(plano: PlanoClubeForm, recebedorConfigurado: boolean): string | null {
    if (plano.ativo && !recebedorConfigurado) {
        return "configure o recebedor Pagar.me com a Cliente Fiel antes de ativar o plano."
    }
    if (!plano.nome.trim()) return "informe o nome do plano."
    const valorMensal = paraNumero(plano.valorMensal)
    if (plano.ativo && (valorMensal === null || valorMensal <= 0)) {
        return "informe o valor mensal."
    }
    if (valorMensal !== null && valorMensal < 0) return "o valor mensal não pode ser negativo."
    for (let i = 0; i < plano.beneficios.length; i++) {
        const b = plano.beneficios[i]
        const n = i + 1
        const qtd = paraNumero(b.quantidade)
        if (qtd === null || qtd < 1 || !Number.isInteger(qtd)) {
            return `benefício ${n}: a quantidade de cupons deve ser um número inteiro maior que zero.`
        }
        if (qtd > QUANTIDADE_MAXIMA_CUPONS) {
            return `benefício ${n}: a quantidade de cupons não pode passar de ${QUANTIDADE_MAXIMA_CUPONS}.`
        }
        const valor = paraNumero(b.valor)
        if (valor === null || valor <= 0) return `benefício ${n}: informe o valor do cupom.`
        if (b.percentual && valor > 100) return `benefício ${n}: o percentual não pode passar de 100%.`
        const minimo = paraNumero(b.valorMinimoPedido)
        if (minimo !== null && minimo < 0) return `benefício ${n}: pedido mínimo inválido.`
        const maximo = paraNumero(b.maximoDesconto)
        if (b.percentual && maximo !== null && maximo < 0) {
            return `benefício ${n}: desconto máximo inválido.`
        }
    }
    if (plano.ativo && !plano.beneficios.some(b => b.ativo)) {
        return "adicione pelo menos um benefício ativo para ativar o plano."
    }
    return null
}

/** Retorna a mensagem do primeiro problema encontrado, ou null se o formulário pode ser salvo. */
export function validarClube(dados: ClubeBeneficiosModel): string | null {
    for (let i = 0; i < dados.planos.length; i++) {
        const erro = validarPlano(dados.planos[i], dados.recebedorConfigurado)
        if (erro) return `${rotuloPlano(dados.planos[i], i)}: ${erro}`
    }
    return null
}

/** Algum plano ativo sem recebedor configurado (o backend recusaria o salvar). */
export function algumPlanoAtivoSemRecebedor(dados: ClubeBeneficiosModel): boolean {
    return !dados.recebedorConfigurado && dados.planos.some(p => p.ativo)
}

/** Valor em reais ou "processando" quando ainda não apurado (null). */
export function formatarValorOuProcessando(n: number | null | undefined): string {
    return n === null || n === undefined ? "processando" : formatarMoeda(n)
}

/** Ordena as mensalidades da mais recente para a mais antiga (pelo pagamento, depois id). */
export function ordenarMensalidades(lista: MensalidadeClube[]): MensalidadeClube[] {
    return [...lista].sort(
        (a, b) => (b.pagoEm ?? 0) - (a.pagoEm ?? 0) || (b.id ?? 0) - (a.id ?? 0)
    )
}

function descreverCupom(b: BeneficioForm): string {
    const valor = paraNumero(b.valor) ?? 0
    return b.percentual ? `${numeroParaTexto(valor)}%` : formatarMoeda(valor)
}

/** "O assinante paga R$ 20,00/mês e recebe 5 cupons de R$ 10,00" */
export function textoPrevia(plano: PlanoClubeForm): string {
    const valorMensal = formatarMoeda(paraNumero(plano.valorMensal) ?? 0)
    const ativos = plano.beneficios.filter(b => b.ativo)
    if (ativos.length === 0) {
        return `O assinante paga ${valorMensal}/mês. Adicione os benefícios que ele recebe.`
    }
    const partes = ativos.map(b => {
        const qtd = Math.trunc(paraNumero(b.quantidade) ?? 0)
        return `${qtd} ${qtd === 1 ? "cupom" : "cupons"} de ${descreverCupom(b)}`
    })
    const lista =
        partes.length === 1
            ? partes[0]
            : `${partes.slice(0, -1).join(", ")} e ${partes[partes.length - 1]}`
    return `O assinante paga ${valorMensal}/mês e recebe ${lista}`
}
